import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ExternalLink, Scissors } from "lucide-react";

/**
 * GarmentsModal — opens the atelier (art-a-porter.com/en/andievargas) in a new tab.
 *
 * Why not an iframe? art-a-porter.com sets `X-Frame-Options: DENY`, which causes
 * Brave / DuckDuckGo / Chrome to refuse to render the page inside an iframe
 * (ERR_BLOCKED_BY_RESPONSE). This is a server-side security header on their end
 * that no client-side JavaScript can override.
 *
 * Instead we show a small "stepping out of the cottage" splash with a prominent
 * Open-in-new-tab CTA. The seeker's primordeia.com tab stays open as the return
 * path, and "Back to Papaya" dismisses the splash.
 */
const GARMENTS_URL = "https://art-a-porter.com/en/andievargas";

const GarmentsModal = ({ open, onClose }) => {
  const openAtelier = () => {
    window.open(GARMENTS_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[480] flex items-center justify-center px-4"
          style={{
            background: "radial-gradient(ellipse at center, rgba(20,9,31,0.92), rgba(5,3,11,0.97))",
            backdropFilter: "blur(8px)",
          }}
          onClick={onClose}
          data-testid="garments-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl p-7 sm:p-9"
            style={{
              background: "linear-gradient(135deg, #1A1030 0%, #2A1845 60%, #1A1030 100%)",
              border: "1px solid rgba(212,165,116,0.4)",
              boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
            }}
          >
            {/* Decorative top icon */}
            <div className="flex items-center justify-center mb-4">
              <motion.div
                animate={{ rotate: [0, -8, 8, -4, 4, 0] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, rgba(212,165,116,0.20), rgba(139,105,20,0.20))",
                  border: "1px solid rgba(212,165,116,0.45)",
                }}
              >
                <Scissors size={20} style={{ color: "#D4A574" }} />
              </motion.div>
            </div>

            {/* Title */}
            <h2
              className="text-center text-2xl sm:text-3xl mb-2"
              style={{
                fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                fontWeight: 700,
                color: "#D4A574",
                letterSpacing: "0.12em",
              }}
              data-testid="garments-modal-title"
            >
              3D GARMENTS
            </h2>
            <p
              className="text-center text-[11px] uppercase mb-5"
              style={{
                color: "#8B7DA8",
                fontFamily: "'Cinzel', serif",
                letterSpacing: "0.32em",
              }}
            >
              andievargas · the atelier
            </p>

            {/* Copy */}
            <p
              className="text-center text-sm leading-relaxed mb-7"
              style={{ color: "#E8E0F8", fontFamily: "'Nunito', sans-serif" }}
            >
              Step out of Berry Kiss Cottage for a moment to visit the seamstress.
              The atelier opens in a new tab — Papaya is right here when you return.
            </p>

            {/* Open atelier — primary CTA */}
            <motion.button
              type="button"
              onClick={openAtelier}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm mb-3"
              style={{
                background: "linear-gradient(135deg, #D4A574 0%, #8B6914 100%)",
                color: "#FFF8E7",
                fontFamily: "'Cinzel', serif",
                letterSpacing: "0.18em",
                boxShadow: "0 8px 24px rgba(212,165,116,0.35)",
              }}
              data-testid="garments-open-atelier"
            >
              <ExternalLink size={14} />
              Open the Atelier
            </motion.button>

            {/* Back to Papaya — obvious return path */}
            <motion.button
              type="button"
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm"
              style={{
                background: "linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)",
                color: "#FFF",
                fontFamily: "'Cinzel', serif",
                letterSpacing: "0.12em",
                boxShadow: "0 6px 20px rgba(79,55,139,0.45)",
              }}
              data-testid="garments-back-button"
              aria-label="Back to Papaya"
            >
              <ArrowLeft size={14} />
              Back to Papaya
            </motion.button>

            {/* Tiny URL preview for trust */}
            <p
              className="text-center text-[10px] italic mt-5"
              style={{ color: "#8B7DA8", fontFamily: "'Nunito', sans-serif" }}
            >
              art-a-porter.com / en / andievargas
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GarmentsModal;
